import {
	Container,
	ContainerGutter,
	ContainerPadding,
	CTA,
	Text,
	TextAlignment,
	TokenTextAppearance,
	Tooltip,
} from '@volkswagen-onehub/components-core';
import { Checkmark, ChevronRight, OpenInLayer } from '@volkswagen-onehub/icons-core';
import { LayerHandleV2 } from '@volkswagen-onehub/layer-manager';
import React from 'react';
import styled from 'styled-components';
import { PortalContext } from '../../context/PortalContext';
import { getSurname1, getSurname2, getUserName } from '../../helpers/nameHelper';
import { formatOwnership } from '../../tracking/trackingHelper';
import { useTrackingManager } from '../../tracking/useTrackingManager';
import { ClubSettingsLayer } from '../club-volkswagen/layer/clubSettingsLayer';
import { ClubVWLogo } from './assets/club-vw-logo';
import { COLOR_TOKEN } from '../../helpers/color-tokens';

const UserInitialsAvatar = styled.div`
	background-color: white;
	position: relative;
	display: flex;
	justify-content: center;
	align-items: center;
	width: 65px;
	height: 65px;
	border-radius: 50%;
	font-size: 80%;
	font-size: 24px;
	color: ${COLOR_TOKEN.content.primary};
	&::after {
		content: '';
		position: absolute;
		width: 100%;
		height: 100%;
		background-color: transparent;
	}
`;

const StyledUnderlineText = styled(Text)`
	text-decoration: underline dashed white;
	text-decoration-thickness: 1px;
	text-underline-offset: 4px;
`;

const MemberIdContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;

	& > div {
		border: 1px solid ${COLOR_TOKEN.content.inverted};
		background: rgba(255, 255, 255, 0.05);
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		max-width: 240px;
		border-radius: 50px;
		cursor: pointer;

		& > span {
			font-family: 'Sometype Mono', monospace;
			font-size: 14px;
			color: ${COLOR_TOKEN.content.inverted};
			padding: 8px 16px;
			display: flex;
			justify-content: center;
			align-items: end;
			gap: 8px;

			& > svg {
				width: 18px;
				height: 18px;
			}
		}
	}
`;

export function UserProfileContainer({ setUpdating }: { setUpdating: React.Dispatch<React.SetStateAction<boolean>> }) {
	const { portalService } = React.useContext(PortalContext);
	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);
	const userSurname1 = getSurname1(userData);
	const userSurname2 = getSurname2(userData);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const trackingManager = useTrackingManager();
	const clubSettingsLayerRef = React.useRef<LayerHandleV2<any>>(null);

	const [copiado, setCopiado] = React.useState(false);
	const [isClubSettingsLayerVisible, setIsClubSettingsVisible] = React.useState<boolean>(false);

	const handleCopyIDMember = () => {
		if (userData) {
			navigator.clipboard.writeText(userData.portalUser.data.clubVW.idMember);
			setCopiado(true);
			setTimeout(() => {
				setCopiado(false);
			}, 1000);
		}
	};

	const handleSettingsVisibilityOnClick = () => {
		trackingManager.trackClubVWButtonClick(
			'Ajustes de mi membresía del Club Volkswagen',
			{
				contentId: 'Open Club Volkswagen settings Layer`',
			},
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);

		trackingManager.trackClubVWLayerLoad(
			{ contentId: 'Club Volkswagen settings Layer' },
			{ vehicle: formatOwnership(userVehicles?.vehicles) },
		);
		setIsClubSettingsVisible(true);
	};

	return (
		<>
			<Container
				data-testid="user-profile-container"
				wrap={'always'}
				padding={{
					top: ContainerPadding.static450,
					right: ContainerPadding.dynamic0100,
					bottom: ContainerPadding.static450,
					left: ContainerPadding.dynamic0100,
				}}
				gutter={ContainerGutter.static400}
			>
				<Container horizontalAlign={'center'}>
					<ClubVWLogo />
				</Container>
				<Container wrap={'always'} gutter={ContainerGutter.dynamic0020}>
					<Container horizontalAlign={'center'}>
						<UserInitialsAvatar>
							{userName.slice(0, 1).toUpperCase() + userSurname1.slice(0, 1).toUpperCase()}
						</UserInitialsAvatar>
					</Container>
					<Container horizontalAlign={'center'}>
						<Text appearance={TokenTextAppearance.headline300} bold>
							{userName} {userSurname1} {userSurname2}
						</Text>
					</Container>
				</Container>
				<Container wrap={'always'} gutter={ContainerGutter.static200}>
					<Container wrap={'auto'} horizontalAlign={'center'}>
						<Text appearance={TokenTextAppearance.copy200} textAlign={TextAlignment.center}>
							Esta es tu{' '}
							<Tooltip
								reducedFontSize
								text="Úsala para disfrutar de nuestro Driving Experience a un precio exclusivo por ser del club."
								uniqueAriaId="tooltip-club-vw"
								position="top"
							>
								<StyledUnderlineText bold>clave del Club Volkswagen</StyledUnderlineText>
							</Tooltip>
						</Text>
					</Container>
					<MemberIdContainer>
						<div className="member-id-button" onClick={handleCopyIDMember}>
							{copiado ? (
								<span>
									¡Copiado! <Checkmark variant="default" aria-hidden />
								</span>
							) : (
								<span>
									{userData?.portalUser.data.clubVW.idMember} <OpenInLayer variant="default" aria-hidden />
								</span>
							)}
						</div>
					</MemberIdContainer>
				</Container>
				<Container wrap={'always'}>
					<Container wrap={'auto'} horizontalAlign={'center'}>
						<CTA tag="a" emphasis="tertiary" onClick={handleSettingsVisibilityOnClick}>
							Ajustes de membresía
							<span style={{ paddingLeft: '5px' }}></span>
							<ChevronRight variant="small" aria-hidden />
						</CTA>
					</Container>
				</Container>
			</Container>
			<ClubSettingsLayer
				userData={userData}
				setIsClubSettingsVisible={setIsClubSettingsVisible}
				isClubSettingsLayerVisible={isClubSettingsLayerVisible}
				clubSettingsLayerRef={clubSettingsLayerRef}
				setUpdating={setUpdating}
			/>
		</>
	);
}
